import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/axios';
import { SHARED_SCHOOL_MOTTO } from '../schoolBranding';
import styles from './Auth.module.css';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, getHomeForRole, user, passwordChangeRequired, firstTimeSetupRequired } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetMode, setResetMode] = useState<'admin' | 'email'>('email');
  const [resetLoginId, setResetLoginId] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [hasRecoveryEmail, setHasRecoveryEmail] = useState<boolean | null>(null);

  const formatRetryAfterMessage = (seconds: number): string => {
    const safeSeconds = Math.max(1, Math.floor(seconds));
    const minutes = Math.ceil(safeSeconds / 60);
    if (minutes >= 60) {
      const hours = Math.ceil(minutes / 60);
      return `Too many login attempts. Please try again in about ${hours} hour${hours === 1 ? '' : 's'}.`;
    }
    return `Too many login attempts. Please try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
  };

  useEffect(() => {
    if (user) {
      if (firstTimeSetupRequired) {
        navigate('/first-time-setup', { replace: true });
      } else if (passwordChangeRequired) {
        navigate('/change-password', { replace: true });
      } else {
        navigate(getHomeForRole(user.role), { replace: true });
      }
    }
  }, [getHomeForRole, navigate, user, passwordChangeRequired, firstTimeSetupRequired]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
    } catch (err: any) {
      const status = err?.response?.status;
      const retryAfter = Number(err?.response?.data?.retry_after ?? err?.response?.headers?.['retry-after']);

      if (status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
        setError(formatRetryAfterMessage(retryAfter));
      } else {
        setError(err?.response?.data?.message ?? 'Login failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetSuccess(false);
    setResetSubmitting(true);
    try {
      if (resetMode === 'email') {
        await api.post('/api/password-reset-email', {
          email: resetLoginId,
        });
        setResetSuccess(true);
        setResetLoginId('');
      } else {
        await api.post('/api/password-reset-request', {
          login_id: resetLoginId,
          reason: resetReason || undefined,
        });
        setResetSuccess(true);
        setResetLoginId('');
        setResetReason('');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetSuccess(false);
        }, 3000);
      }
    } catch (err: any) {
      setResetError(err?.response?.data?.message ?? 'Failed to submit password reset request');
      if (err?.response?.data?.has_recovery_email === false) {
        setHasRecoveryEmail(false);
      }
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
    setResetLoginId('');
    setResetReason('');
    setResetError(null);
    setResetSuccess(false);
    setHasRecoveryEmail(null);
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.dualCardContainer}>
        {/* Login Card */}
        <div className={styles.card}>
          <div className={styles.logoSection}>
            <h1 className={styles.logoTitle}>KBxLC</h1>
            <p className={styles.logoMotto}>{SHARED_SCHOOL_MOTTO}</p>
          </div>

          {/* School Info */}
          <div style={{
            display: 'flex',
            gap: 8,
            marginBottom: 20,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{
              padding: '8px 14px',
              background: '#1e3a5f',
              borderRadius: 8,
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <img 
                src="/kba logo.PNG" 
                alt="KBA Logo"
                style={{ width: '18px', height: '18px', objectFit: 'contain' }}
              /> KBA - Secondary
            </div>
            <div style={{
              padding: '8px 14px',
              background: '#15803d',
              borderRadius: 8,
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <img 
                src="/tlc logo.jpg" 
                alt="TLC Logo"
                style={{ width: '18px', height: '18px', objectFit: 'contain' }}
              /> TLC - Primary
            </div>
          </div>

          <form onSubmit={onSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>User ID</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className={styles.formInput}
                placeholder="e.g., KBA T01, TLC S001"
              />
              
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="login-password">
                Password
              </label>
              <div className={styles.passwordFieldWrap}>
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`${styles.formInput} ${styles.formInputPassword}`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {error && (
              <div className={styles.errorMessage}>
                ⚠️ {error}
              </div>
            )}

            <button 
              type="submit" 
              disabled={submitting} 
              className={styles.primaryButton}
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>

            <div style={{ textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(!showForgotPassword)}
                className={styles.linkButton}
              >
                Forgot Password?
              </button>
            </div>
          </form>
        </div>

        {/* Forgot Password Card */}
        {showForgotPassword && (
          <div className={styles.forgotPasswordCard}>
            <h2 className={styles.sectionTitle}>Reset Password</h2>
            
            {/* Reset mode toggle */}
            <div className={styles.tabContainer}>
              <button
                type="button"
                onClick={() => {
                  setResetMode('email');
                  setResetError(null);
                  setResetSuccess(false);
                  setHasRecoveryEmail(null);
                }}
                className={`${styles.tab} ${resetMode === 'email' ? styles.active : ''}`}
              >
                📧 Via Email
              </button>
              <button
                type="button"
                onClick={() => {
                  setResetMode('admin');
                  setResetError(null);
                  setResetSuccess(false);
                  setHasRecoveryEmail(null);
                }}
                className={`${styles.tab} ${resetMode === 'admin' ? styles.active : ''}`}
              >
                👤 Via Admin
              </button>
            </div>

            <p className={styles.sectionSubtitle} style={{ marginBottom: 20 }}>
              {resetMode === 'email' 
                ? 'Enter your email address to receive a password reset link.'
                : 'Submit a request to the admin. You will need to contact them in person to complete the process.'}
            </p>
            
            <form onSubmit={onSubmitPasswordReset} className={styles.form}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {resetMode === 'email' ? 'Email Address' : 'User ID'}
                </label>
                <input
                  type={resetMode === 'email' ? 'email' : 'text'}
                  value={resetLoginId}
                  onChange={(e) => setResetLoginId(e.target.value)}
                  required
                  className={styles.formInput}
                  placeholder={resetMode === 'email' ? 'your.email@example.com' : 'e.g., KBA S001, KBA T01'}
                />
              </div>
              
              {resetMode === 'admin' && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Reason (Optional)</label>
                  <textarea
                    value={resetReason}
                    onChange={(e) => setResetReason(e.target.value)}
                    className={styles.formTextarea}
                    placeholder="Optional: Explain why you need a password reset"
                    maxLength={500}
                  />
                </div>
              )}

              {hasRecoveryEmail === false && (
                <div className={styles.warningMessage}>
                  ⚠️ This account doesn't have a recovery email. Please use the "Via Admin" option instead.
                </div>
              )}
              
              {resetSuccess && (
                <div className={styles.successMessage}>
                  ✅ {resetMode === 'email' 
                    ? 'Reset link sent! Check your email inbox and click the link to reset your password.'
                    : 'Request submitted successfully! Please contact the admin in person to complete the password reset.'}
                </div>
              )}
              
              {resetError && (
                <div className={styles.errorMessage}>
                  ⚠️ {resetError}
                </div>
              )}

              <button
                type="submit"
                disabled={resetSubmitting}
                className={styles.primaryButton}
              >
                {resetSubmitting ? 'Submitting...' : (resetMode === 'email' ? 'Send Reset Link' : 'Submit Request')}
              </button>
              
              <button
                type="button"
                onClick={handleCloseForgotPassword}
                className={styles.secondaryButton}
              >
                Cancel
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
